import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Linkedin, 
  Twitter, 
  Facebook, 
  Mail, 
  Phone, 
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gita-enterprise/artifacts/18uc5tiv_Global%20IT%20-%20Logo.jpg";

const footerLinks = {
  services: [
    { label: "IT Staffing & Talent", href: "/services/staffing" },
    { label: "AI & Automation", href: "/services/ai-automation" },
    { label: "IT Consulting", href: "/services/consulting" },
    { label: "Cloud & DevOps", href: "/services/cloud-devops" },
    { label: "Data & Analytics", href: "/services/data-analytics" },
  ],
  company: [
    { label: "About Us", href: "/about" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/contact" },
  ],
  resources: [
    { label: "Blog", href: "#" },
    { label: "Insights", href: "#" },
    { label: "Events", href: "#" },
    { label: "Partners", href: "#" },
  ],
  legal: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" },
    { label: "Cookie Policy", href: "#" },
  ]
};

const socialLinks = [
  { icon: Linkedin, href: "https://linkedin.com/company/globalitassociates", label: "LinkedIn" },
  { icon: Twitter, href: "https://twitter.com/gitallc", label: "Twitter" },
  { icon: Facebook, href: "https://facebook.com/globalitassociates", label: "Facebook" },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="footer-dark text-white" data-testid="main-footer">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="container-padding py-16">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-md">
              <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Stay Informed
              </h3>
              <p className="text-slate-400">
                Get the latest insights on IT staffing, AI trends, and enterprise technology.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" onSubmit={(e) => e.preventDefault()}>
              <Input 
                type="email" 
                placeholder="Enter your email"
                className="h-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-[#2563EB] sm:w-72"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                className="h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6"
                data-testid="newsletter-subscribe-btn"
              >
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-padding py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo-link">
              <img 
                src={LOGO_URL} 
                alt="Global IT Associates" 
                className="h-16 w-auto"
                loading="lazy"
              />
            </Link>
            <p className="text-slate-400 mb-6 leading-relaxed">
              Combining elite talent with AI-driven solutions to accelerate business growth. 
              Your trusted partner for IT staffing and technology consulting.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a 
                href="mailto:info@gitallc.com" 
                className="flex items-center text-slate-400 hover:text-white transition-colors"
                data-testid="footer-email-link"
              >
                <Mail className="w-5 h-5 mr-3 text-[#2563EB]" />
                info@gitallc.com
              </a>
              <a 
                href="tel:+1-718-554-3222" 
                className="flex items-center text-slate-400 hover:text-white transition-colors"
                data-testid="footer-phone-link"
              >
                <Phone className="w-5 h-5 mr-3 text-[#2563EB]" />
                718-554-3222
              </a>
              <div className="flex items-start text-slate-400">
                <MapPin className="w-5 h-5 mr-3 text-[#2563EB] flex-shrink-0 mt-0.5" />
                <span>1110 South Ave<br />Staten Island, NY 10314</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-4 mt-6">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-[#2563EB] hover:text-white transition-all"
                  aria-label={social.label}
                  data-testid={`footer-social-${social.label.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="footer-link text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="footer-link text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="footer-link text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="footer-link text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="container-padding py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-slate-500">
            <p data-testid="footer-copyright">
              &copy; {currentYear} Global IT Associates, LLC. All rights reserved.
            </p>
            <p>
              Technology & Beyond
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
